/**
 * ATOM LED -- Cookie Consent Banner
 * PECR/UK-GDPR compliant via Shopify Consent Tracking API.
 * No innerHTML. No external deps. Deferred script.
 *
 * Preview mode: append ?cb_preview=1 to force-show the banner
 * without enabling Customer Privacy regions in Shopify admin.
 * Production visitors (no param) use shouldShowBanner() gating.
 */
(function () {
  'use strict';

  var banner = document.getElementById('atom-cookie-banner');
  var panel = document.getElementById('atom-cookie-panel');
  if (!banner || !panel) return;

  var analyticsToggle = document.getElementById('cb-analytics');
  var marketingToggle = document.getElementById('cb-marketing');

  // ── Preview mode flag ──
  var isPreview = new URLSearchParams(location.search).get('cb_preview') === '1';

  // ── Shopify Consent Tracking API bootstrap ──
  function loadConsentAPI(callback) {
    if (
      window.Shopify &&
      window.Shopify.loadFeatures
    ) {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (err) {
          if (err) {
            if (window.ATOM_DEBUG) console.warn('[ATOM Cookie] Consent API failed to load:', err);
            return;
          }
          callback();
        }
      );
    } else {
      if (window.ATOM_DEBUG) console.warn('[ATOM Cookie] Shopify.loadFeatures unavailable -- banner will not show.');
    }
  }

  // ── Show/hide helpers ──
  function showBanner() {
    banner.hidden = false;
    banner.setAttribute('aria-hidden', 'false');
    // Focus first button for a11y
    var first = banner.querySelector('button');
    if (first) first.focus();
  }

  function hideBanner() {
    banner.hidden = true;
    banner.setAttribute('aria-hidden', 'true');
    hidePanel();
  }

  function showPanel() {
    panel.hidden = false;
    panel.setAttribute('aria-hidden', 'false');
    if (btnSettings) btnSettings.setAttribute('aria-expanded', 'true');
  }

  function hidePanel() {
    panel.hidden = true;
    panel.setAttribute('aria-hidden', 'true');
    if (btnSettings) btnSettings.setAttribute('aria-expanded', 'false');
  }

  // ── First-party choice store ──────────────────────────────────────────
  // Shopify's setTrackingConsent() does not reliably persist the _tracking_consent
  // cookie on this shop (verified 2026-06-11: callback fires, cookie never written,
  // shouldShowBanner() stays true) — so the banner re-appeared on every page. We
  // record the visitor's choice in our own strictly-necessary first-party cookie
  // (exempt from consent under PECR) and re-apply it to the Consent API on every
  // load, so the banner shows once and a Reject is actually honoured.
  var CHOICE_COOKIE = '_atom_cb';
  var CHOICE_MAX_AGE = 60 * 60 * 24 * 180; // 180 days

  function writeChoice(analytics, marketing) {
    var val = (analytics ? '1' : '0') + (marketing ? '1' : '0');
    document.cookie =
      CHOICE_COOKIE + '=' + val + ';path=/;max-age=' + CHOICE_MAX_AGE + ';SameSite=Lax';
  }

  function readChoice() {
    var m = document.cookie.match(/(?:^|;\s*)_atom_cb=([01])([01])/);
    if (!m) return null;
    return { analytics: m[1] === '1', marketing: m[2] === '1' };
  }

  function applyConsent(cp, analytics, marketing, done) {
    if (cp && typeof cp.setTrackingConsent === 'function') {
      try {
        cp.setTrackingConsent(
          {
            analytics: analytics,
            marketing: marketing,
            preferences: analytics, // preferences follow analytics
            sale_of_data: false
          },
          function () { if (done) done(); }
        );
        return;
      } catch (e) { /* fall through */ }
    }
    if (done) done();
  }

  // ── Consent setters ──
  function setConsent(analytics, marketing) {
    writeChoice(analytics, marketing); // persist FIRST so the banner never re-nags
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    applyConsent(cp, analytics, marketing, hideBanner);
  }

  // ── Event wiring (no innerHTML, all static elements via Liquid) ──
  var btnAccept = banner.querySelector('[data-cb-accept]');
  var btnReject = banner.querySelector('[data-cb-reject]');
  var btnSettings = banner.querySelector('[data-cb-settings]');
  var btnSave = banner.querySelector('[data-cb-save]');

  if (btnAccept) {
    btnAccept.addEventListener('click', function () {
      setConsent(true, true);
    });
  }

  if (btnReject) {
    btnReject.addEventListener('click', function () {
      setConsent(false, false);
    });
  }

  if (btnSettings) {
    btnSettings.addEventListener('click', function () {
      var isVisible = !panel.hidden;
      if (isVisible) {
        hidePanel();
      } else {
        showPanel();
      }
    });
  }

  if (btnSave) {
    btnSave.addEventListener('click', function () {
      var a = analyticsToggle ? analyticsToggle.checked : false;
      var m = marketingToggle ? marketingToggle.checked : false;
      setConsent(a, m);
    });
  }

  // ── Keyboard: Esc closes panel (not banner -- banner stays until choice) ──
  banner.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !panel.hidden) {
      hidePanel();
      if (btnSettings) btnSettings.focus();
    }
  });

  // ── Footer re-open listener ──
  // Any element with data-cb-reopen dispatches a custom event to re-show
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-cb-reopen]');
    if (trigger) {
      e.preventDefault();
      showBanner();
    }
  });

  // Also listen for a custom event (programmatic re-open)
  document.addEventListener('atom:cookie-settings-open', function () {
    showBanner();
    showPanel();
  });

  // ── Init ──

  // PREVIEW MODE: force-show immediately, skip shouldShowBanner() entirely.
  // Does not depend on the Consent API being loaded or configured.
  if (isPreview) {
    showBanner();
    return; // skip the production path below
  }

  // PRODUCTION
  loadConsentAPI(function () {
    var cp = window.Shopify && window.Shopify.customerPrivacy;
    if (!cp) {
      if (window.ATOM_DEBUG) console.warn('[ATOM Cookie] customerPrivacy object unavailable after load.');
      return;
    }

    // If this visitor already chose, re-apply their choice to the Consent API
    // (Shopify doesn't persist it on this shop) and NEVER show the banner again.
    var saved = readChoice();
    if (saved) {
      applyConsent(cp, saved.analytics, saved.marketing);
      return;
    }

    // No prior choice → show the banner when the region requires it.
    if (typeof cp.shouldShowBanner === 'function' && cp.shouldShowBanner()) {
      showBanner();
    }
  });
})();
